import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";

import { BannerSectionBlock } from "../../blocks/BannerSectionBlock";
import { OfferSectionBlock } from "../../blocks/OfferSectionBlock";

/**
 * toppers OfferListPage body assembly.
 *
 * FAITHFUL port of the ``offer_list_page.html`` body loop, which is NOT a
 * generic ``{% include_block %}`` loop -- it is a
 * hand-written ``{% for block in page.body %}`` with custom ``.offer-list``
 * chrome and per-type ``{% if block.block_type == X %}`` branches (NO ``else``):
 *
 *   {% for block in page.body %}
 *     {% if forloop.first %}<div class="offer-list">{% endif %}
 *     {% if block.block_type == 'banner_section' %}{% include_block block %}{% endif %}
 *     {% if block.block_type == 'heading' %}
 *       <div class="offer-list__cat-title"><h1>{{ block.value }}</h1></div>
 *     {% endif %}
 *     {% if block.block_type == 'offer_section' %}{% include_block block %}{% endif %}
 *     {% if forloop.last %}</div>{% endif %}
 *   {% endfor %}
 *
 * CONSEQUENCE (intentional, faithful): only ``banner_section``, ``heading`` and
 * ``offer_section`` render. The 11 Toolbox keys (banner, promotion_block,
 * email_capture, promo_conditional_block, etc.) are part of the StreamField type
 * but fall through every ``{% if %}`` with no ``else``, so the template
 * rendered NOTHING for them. We reproduce that drop exactly (a ``default: null``
 * branch) rather than wiring the toolbox map.
 *
 * The single ``.offer-list`` wrapper is emitted only when the body is non-empty
 * (opened it on ``forloop.first`` / closed on ``forloop.last``).
 *
 * NOTE: the template's ``self.value_props`` pre-loop reads a SEPARATE
 * page field (not ``page.body``) and is therefore out of scope for this body
 * assembly.
 */
export const OfferListPageBody = (
    props: Immutable<{
        page: Pick<templates.OfferListPageTemplate["page"], "body">;
    }>,
): React.JSX.Element | null => {
    const body = props.page.body;
    if (!body || body.value.length === 0) {
        return null;
    }
    return (
        <div className="offer-list">
            {body.value.map((block) => {
                switch (block.type) {
                    case "banner_section":
                        return (
                            <BannerSectionBlock
                                key={block.id}
                                value={block.value}
                            />
                        );
                    case "heading":
                        return (
                            <div
                                key={block.id}
                                className="offer-list__cat-title"
                            >
                                <h1>{block.value}</h1>
                            </div>
                        );
                    case "offer_section":
                        return (
                            <OfferSectionBlock
                                key={block.id}
                                value={block.value}
                            />
                        );
                    default:
                        // All Toolbox keys: the loop has no `else`, so
                        // they rendered nothing. Reproduced faithfully.
                        return null;
                }
            })}
        </div>
    );
};
